import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Dialog from "@/components/atoms/Dialog";
import { usersService } from "@/services/usersService";
import type { UserSummary } from "@/types/entities";
import { showActionErrorFromUnknown } from "@/lib/feedback";

interface Props {
  open: boolean;
  user: UserSummary | null;
  onClose: () => void;
  onSaved: () => void;
}

const INTENT_OPTIONS = [
  { value: "Lettings", label: "Lettings (e.g. LookingToRent, RENT)" },
  { value: "Sales", label: "Sales (e.g. LookingToBuy, Buy)" },
  { value: "Lettings valuation", label: "Lettings valuation (e.g. LookingToLet)" },
  { value: "Sales valuation", label: "Sales valuation (e.g. LookingToSell)" },
  { value: "General", label: "General (e.g. Unspecified, Other)" },
];

export function EditUserIntentDialog({ open, user, onClose, onSaved }: Props) {
  const [selectedIntents, setSelectedIntents] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setSelectedIntents(user.userIntent ?? []);
    }
  }, [open, user]);

  const toggleIntent = (value: string) => {
    setSelectedIntents((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
    );
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const fullUser = await usersService.getOne(user.id);
      await usersService.update(user.id, {
        ...fullUser,
        userIntent: selectedIntents,
      });
      toast.success("User intent updated");
      onSaved();
    } catch (e) {
      showActionErrorFromUnknown(e, "Failed to update user intent");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title="Edit User Intent"
      submitLabel={saving ? "Saving..." : "Save changes"}
    >
      <div className="py-2 space-y-4">
        <p className="text-sm text-bridge-subtle">
          Select the market types this user handles. This will be used to automatically assign bookings to the right users based on the property market type.
        </p>
        
        <div className="space-y-3">
          {INTENT_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-bridge-border/60 bg-bridge-surface text-bridge-accent focus:ring-bridge-accent"
                checked={selectedIntents.includes(opt.value)}
                onChange={() => toggleIntent(opt.value)}
              />
              <span className="text-sm text-bridge-fg leading-none mt-1">
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
